/* ==========================================================================
   Lisbon Vice - Plan bar
   --------------------------------------------------------------------------
   Reads the Shopify cart and mirrors it into a fixed bottom bar. The cart is
   the plan, so there is no second source of truth to keep in sync.

   It refreshes on three signals:
     1. page load
     2. the lv:cart:updated event fired by the activity grid
     3. any fetch or XHR the page makes to a /cart/{add,change,update,clear}
        endpoint, which is how the theme's own add-to-cart is caught without
        depending on Beyond internals that could change under us
   ========================================================================== */

(function () {
  'use strict';

  if (window.__lvPlanBarBound) return;
  window.__lvPlanBarBound = true;

  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  function root() {
    return document.querySelector('[data-lv-planbar]');
  }

  function cartJsonUrl() {
    var prefix =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return prefix.replace(/\/$/, '') + '/cart.js';
  }

  function formatMoney(cents, currency) {
    var amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2);
    }
  }

  function render(cart) {
    var bar = root();
    if (!bar || !cart) return;

    var count = cart.item_count || 0;
    var isEmpty = count === 0;

    bar.setAttribute('data-empty', isEmpty ? 'true' : 'false');
    bar.setAttribute('aria-hidden', isEmpty ? 'true' : 'false');

    var countEl = bar.querySelector('[data-lv-planbar-count]');
    if (countEl) {
      countEl.textContent =
        count + ' ' + (count === 1 ? bar.dataset.wordOne : bar.dataset.wordMany);
    }

    var totalEl = bar.querySelector('[data-lv-planbar-total]');
    if (totalEl) {
      totalEl.textContent =
        (bar.dataset.totalPrefix || '') + formatMoney(cart.total_price, cart.currency);
    }

    document.body.classList.toggle('lv-planbar-visible', !isEmpty);

    if (!isEmpty) {
      document.body.style.setProperty('--lv-planbar-space', bar.offsetHeight + 'px');
    } else {
      document.body.style.removeProperty('--lv-planbar-space');
    }
  }

  var pending = null;

  function refresh() {
    if (!root()) return Promise.resolve(null);
    if (pending) return pending;

    pending = fetch(cartJsonUrl(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        render(cart);
        return cart;
      })
      .catch(function () {
        return null;
      })
      .then(function (cart) {
        pending = null;
        return cart;
      });

    return pending;
  }

  /* --- Primary action -----------------------------------------------------
     The bar's primary button is still a real <a href="/cart">. That matters:
     with JavaScript off, or if the header is not rendered on some template,
     the link keeps working and the visitor still reaches a cart.

     When the header cart trigger is present the click is intercepted and the
     drawer is opened instead, because the quote request form only exists in
     the drawer view of snippets/cart. The /cart page renders the desktop and
     mobile views, which show Beyond's plain checkout button, not the form.
     --------------------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    var link = event.target.closest('[data-lv-planbar-drawer="true"]');
    if (!link) return;

    var trigger = document.querySelector('.header--cart');
    if (!trigger) return; // No drawer on this page: let the href do its job.

    event.preventDefault();
    trigger.click();
  });

  /* --- Signals ------------------------------------------------------------ */

  document.addEventListener('lv:cart:updated', function (event) {
    if (event.detail && event.detail.cart) {
      render(event.detail.cart);
    } else {
      refresh();
    }
  });

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector('[data-lv-planbar]')) refresh();
  });

  // Catch cart writes made by the rest of the theme without reaching into it.
  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var url = '';
      try {
        url = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url;
      } catch (e) {
        url = '';
      }

      var result = nativeFetch.apply(this, args);
      if (url && CART_WRITE.test(url)) {
        result
          .then(function () {
            refresh();
          })
          .catch(function () {});
      }
      return result;
    };
  }

  if (window.XMLHttpRequest) {
    var nativeOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (url && CART_WRITE.test(String(url))) {
        this.addEventListener('load', function () {
          refresh();
        });
      }
      return nativeOpen.apply(this, arguments);
    };
  }

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refresh();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
